import get from 'lodash-es/get';
import { useState } from 'react';
import { Control, FieldError, FieldPath, UseFormSetValue, UseFormWatch, useFormState } from 'react-hook-form';
import {
  ComboboxField,
  CustomTextInputField,
  DatePickerField,
  SelectField,
  TextInputField,
} from '@brighte/form-components';
import { AU_STATES_CODE, IdentificationType, IdentityType, isBusinessLoanProduct } from '@brighte/validation';
import { useApplicationData } from '@provider';
import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { RadioCard, RadioGroup } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { IDENTIFICATION, LABELS, PLACEHOLDER } from '@utils';
import {
  AUSTRALIA_ISSUING_COUNTRY,
  PASSPORT_ISSUER,
  PassportIssuer,
  getCountryCode,
  getExpiryDateFieldName,
  getIdentificationOptions,
} from './helpers';

type Props = {
  control: Control<IdentityType>;
  watch: UseFormWatch<IdentityType>;
  setValue: UseFormSetValue<IdentityType>;
  name: string;
  isExistingUser?: boolean;
};

function IdentificationForm({ control, watch, setValue, name, isExistingUser = false }: Props) {
  const {
    IDENTIFICATION_TYPE,
    FIRST_NAME,
    MIDDLE_NAME,
    LAST_NAME,
    MEDICARE_NUMBER,
    MEDICARE_REFERENCE,
    PASSPORT_NUMBER,
    ISSUING_COUNTRY,
    LICENCE_NUMBER,
    LICENCE_CARD_NUMBER,
    EXPIRY_DATE,
    DATE_OF_BIRTH,
    PHOTO_ID_NUMBER,
    ISSUING_COUNTRY_PLACEHOLDER,
    AUSTRALIAN_PASSPORT,
    OVERSEAS_PASSPORT,
    EMAIL_ADDRESS,
    PHONE_NUMBER,
    DRIVER_LICENCE_NUMBER,
  } = LABELS;
  const {
    MEDICARE_CARD_COLOUR_OPTIONS,
    MEDICARE_CARD_DESCRIPTION,
    MEDICARE_REF_DESCRIPTION,
    IDENTIFICATION_LICENCE_DESCRIPTION,
    DEFAULT_LICENCE_NUMBER_DESCRIPTION,
    DEFAULT_LICENCE_CARD_NUMBER_DESCRIPTION,
    ISSUE_STATE,
    STATE_OR_TERRITORY,
    CARD_COLOUR,
  } = IDENTIFICATION;

  const { quote } = useApplicationData(['quote']);
  const financialProductId = quote?.financialProduct?.id ?? '';
  const identificationType = watch(`${name}.identification.type`);
  const stateCode = watch(`${name}.identification.drivingLicense.state`);

  // The passport issuing country is captured via a radio (majority are Australian); the country
  // dropdown is only shown for overseas passports, and Australian auto-fills the country. The
  // `issuingCountry` ComboboxField below stays mounted (just hidden for non-overseas) because the form
  // uses `shouldUnregister`, which would otherwise discard the auto-filled Australian value the moment
  // the dropdown unmounts — blocking Save & Continue.
  const passportIssuingCountryField = `${name}.identification.passport.issuingCountry` as FieldPath<IdentityType>;
  const issuingCountry = watch(passportIssuingCountryField);

  // Australian is read back from the form value rather than held in state. Switching the identification
  // type away and back unmounts this whole block, so `shouldUnregister` drops the country with it —
  // state would leave the radio showing an answer whose value is gone, and re-picking the same option
  // fires no change event to write it back, stranding the applicant on a required error they cannot
  // clear. Only Overseas needs state, because it blanks the country deliberately; if that value is
  // dropped the applicant sees the empty country dropdown and can pick again.
  const [isOverseasPassport, setIsOverseasPassport] = useState(
    () => Boolean(issuingCountry) && issuingCountry !== AUSTRALIA_ISSUING_COUNTRY,
  );
  const passportIssuer: PassportIssuer | '' = isOverseasPassport
    ? PASSPORT_ISSUER.OVERSEAS
    : issuingCountry === AUSTRALIA_ISSUING_COUNTRY
      ? PASSPORT_ISSUER.AUSTRALIA
      : '';

  // When the dropdown is hidden its required error can't be seen, so surface it on the radio instead
  // (e.g. the applicant tried to continue without choosing Australian/Overseas). Read the error off
  // useFormState rather than taking getFieldState as a prop — a caller that forgot to pass it would
  // leave the applicant with a submit that does nothing and no visible reason why.
  const { errors } = useFormState({ control, name: passportIssuingCountryField });
  const issuingCountryError: FieldError | undefined = get(errors, passportIssuingCountryField);
  const showIssuingCountryErrorOnRadio = !isOverseasPassport && Boolean(issuingCountryError);

  const handlePassportIssuerChange = (value: PassportIssuer | '') => {
    const isAustralianPassport = value === PASSPORT_ISSUER.AUSTRALIA;

    setIsOverseasPassport(value === PASSPORT_ISSUER.OVERSEAS);
    setValue(
      passportIssuingCountryField,
      isAustralianPassport ? AUSTRALIA_ISSUING_COUNTRY : '',
      // Validate only when Australian fills the country in, which clears any stale required error.
      // Choosing Overseas blanks the country on purpose, and validating there would flag the field as
      // an error the instant the applicant picks the option — before they can possibly have answered it.
      { shouldValidate: isAustralianPassport },
    );
  };

  const expiryDateField = getExpiryDateFieldName(identificationType, name);
  const dob = watch(`${name}.dateOfBirth`);
  // dob field is optional for user is existing user. We don't want to disable empty dob field
  const isExistingUserDob = dob && isExistingUser;
  const isSme = isBusinessLoanProduct(financialProductId);

  return (
    <>
      {!isSme ? (
        <SelectField
          name={`${name}.identification.type`}
          control={control}
          label={IDENTIFICATION_TYPE}
          options={getIdentificationOptions(financialProductId)}
          placeholder={PLACEHOLDER}
        />
      ) : null}
      {identificationType ? (
        <>
          {identificationType === IdentificationType.MEDICARE ? (
            <SelectField
              name={`${name}.identification.medicare.color`}
              control={control}
              label={CARD_COLOUR}
              options={MEDICARE_CARD_COLOUR_OPTIONS}
              placeholder={PLACEHOLDER}
            />
          ) : null}
          {identificationType === IdentificationType.DRIVING_LICENSE && !isSme ? (
            <SelectField
              name={`${name}.identification.drivingLicense.state`}
              control={control}
              label={STATE_OR_TERRITORY}
              options={AU_STATES_CODE}
              placeholder={PLACEHOLDER}
            />
          ) : null}
          {identificationType === IdentificationType.PHOTO_ID ? (
            <>
              <TextInputField
                name={`${name}.identification.photoId.number`}
                control={control}
                label={PHOTO_ID_NUMBER}
                autoComplete="off"
              />
              <SelectField
                name={`${name}.identification.photoId.state`}
                control={control}
                label={STATE_OR_TERRITORY}
                options={AU_STATES_CODE}
                placeholder={PLACEHOLDER}
              />
            </>
          ) : null}
          <TextInputField
            name={`${name}.firstName`}
            control={control}
            label={FIRST_NAME}
            FieldProps={{
              disabled: isExistingUser,
            }}
          />
          <TextInputField
            name={`${name}.middleName`}
            control={control}
            label={MIDDLE_NAME}
            FieldProps={{ secondaryLabel: '(Optional)', disabled: isExistingUser }}
          />
          <TextInputField
            name={`${name}.lastName`}
            control={control}
            label={LAST_NAME}
            FieldProps={{
              disabled: isExistingUser,
            }}
          />
          {isSme ? (
            <>
              <TextInputField name={`${name}.email`} control={control} label={EMAIL_ADDRESS} />
              <TextInputField name={`${name}.phone`} control={control} label={PHONE_NUMBER} />
            </>
          ) : null}

          {identificationType === IdentificationType.MEDICARE ? (
            <>
              <TextInputField
                name={`${name}.identification.medicare.number`}
                control={control}
                label={MEDICARE_NUMBER}
                FieldProps={{ description: MEDICARE_CARD_DESCRIPTION }}
                autoComplete="off"
              />
              <TextInputField
                name={`${name}.identification.medicare.reference`}
                control={control}
                label={MEDICARE_REFERENCE}
                FieldProps={{ description: MEDICARE_REF_DESCRIPTION }}
              />
            </>
          ) : null}
          {identificationType === IdentificationType.PASSPORT ? (
            <>
              <TextInputField
                name={`${name}.identification.passport.number`}
                control={control}
                label={PASSPORT_NUMBER}
                autoComplete="off"
              />
              {/* 16px gap between the radio cards and the (conditionally shown) country dropdown. */}
              <Stack gap="large">
                <Field
                  label={ISSUING_COUNTRY}
                  tone={showIssuingCountryErrorOnRadio ? 'critical' : undefined}
                  message={showIssuingCountryErrorOnRadio ? issuingCountryError?.message : undefined}
                >
                  <RadioGroup
                    name={`${name}.identification.passport.issuer`}
                    value={passportIssuer}
                    onChange={handlePassportIssuerChange}
                  >
                    {/* Stack the two options vertically on mobile, side by side from tablet up. */}
                    <Columns gap="small" collapseBelow="tablet">
                      <RadioCard value={PASSPORT_ISSUER.AUSTRALIA}>
                        <Text>{AUSTRALIAN_PASSPORT}</Text>
                      </RadioCard>
                      <RadioCard value={PASSPORT_ISSUER.OVERSEAS}>
                        <Text>{OVERSEAS_PASSPORT}</Text>
                      </RadioCard>
                    </Columns>
                  </RadioGroup>
                </Field>
                {/* Stays mounted (hidden for non-overseas) so `shouldUnregister` doesn't drop the value. */}
                <Box display={isOverseasPassport ? 'block' : 'none'}>
                  <ComboboxField
                    name={`${name}.identification.passport.issuingCountry`}
                    control={control}
                    label={ISSUING_COUNTRY}
                    FieldProps={{ labelVisibility: 'hidden' }}
                    items={getCountryCode()}
                    placeholder={ISSUING_COUNTRY_PLACEHOLDER}
                    getOptionValue={option => option.label}
                  />
                </Box>
              </Stack>
            </>
          ) : null}
          {identificationType === IdentificationType.DRIVING_LICENSE && (isSme || stateCode) ? (
            <>
              <TextInputField
                name={`${name}.identification.drivingLicense.number`}
                control={control}
                label={isSme ? DRIVER_LICENCE_NUMBER : LICENCE_NUMBER}
                FieldProps={{
                  description: stateCode
                    ? IDENTIFICATION_LICENCE_DESCRIPTION[stateCode].LICENCE_NUMBER_DESCRIPTION
                    : DEFAULT_LICENCE_NUMBER_DESCRIPTION,
                }}
                autoComplete="off"
              />
              <TextInputField
                name={`${name}.identification.drivingLicense.cardNumber`}
                control={control}
                label={LICENCE_CARD_NUMBER}
                FieldProps={{
                  description: stateCode
                    ? IDENTIFICATION_LICENCE_DESCRIPTION[stateCode].LICENCE_CARD_NUMBER_DESCRIPTION
                    : DEFAULT_LICENCE_CARD_NUMBER_DESCRIPTION,
                }}
                autoComplete="off"
              />
            </>
          ) : null}

          {isSme && identificationType === IdentificationType.DRIVING_LICENSE ? (
            <SelectField
              name={`${name}.identification.drivingLicense.state`}
              control={control}
              label={ISSUE_STATE}
              options={AU_STATES_CODE}
              placeholder="State or Territory"
            />
          ) : null}
          <Columns gap="xxlarge" collapseBelow="tablet" template={expiryDateField ? [1, 1] : [1]}>
            {expiryDateField ? (
              identificationType === IdentificationType.MEDICARE ? (
                <CustomTextInputField
                  name={expiryDateField}
                  control={control}
                  label={EXPIRY_DATE}
                  placeholder="MM/YYYY"
                  FieldProps={{ description: 'Enter expiry in MM/YYYY format' }}
                  autoComplete="off"
                  mask="mm/yyyy"
                />
              ) : (
                <DatePickerField name={expiryDateField} control={control} label={EXPIRY_DATE} autoComplete="off" />
              )
            ) : null}
            <DatePickerField
              name={`${name}.dateOfBirth`}
              control={control}
              label={DATE_OF_BIRTH}
              autoComplete="off"
              FieldProps={{
                disabled: isExistingUserDob,
                // \u00A0 is non-breaking space to align fields for medicare card.
                ...(identificationType === IdentificationType.MEDICARE && { description: '\u00A0' }),
              }}
            />
          </Columns>
        </>
      ) : null}
    </>
  );
}

export default IdentificationForm;
